/**
 * Damage Cover Cart Management
 * Handles adding damage cover products to stitching sets in cart
 */

class DamageCoverCart {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Handle damage cover add button clicks
    document.addEventListener('click', (e) => {
      const addButton = e.target.closest('.damage-cover-button--add');
      if (addButton) {
        e.preventDefault();
        this.handleAddDamageCover(addButton);
      }

      // Handle stitching set remove button clicks
      const stitchingRemoveButton = e.target.closest('.cart__item--stitching-set .cart__item-remove');
      if (stitchingRemoveButton) {
        // Get the stitching set ID from the container
        const stitchingSetContainer = stitchingRemoveButton.closest('.cart__item--stitching-set');
        if (stitchingSetContainer) {
          const stitchingSetId = stitchingSetContainer.dataset.stitchingSetId;
          if (stitchingSetId) {
            // Schedule damage cover removal after stitching set removal
            setTimeout(() => {
              this.handleStitchingSetRemoval(stitchingSetId);
            }, 500); // Give time for Shopify to process the main removal
          }
        }
      }
    });
  }

  async handleAddDamageCover(button) {
    const variantId = button.dataset.variantId;
    const stitchingSetId = button.dataset.stitchingSetId;
    
    if (!variantId || !stitchingSetId) {
      console.error('Missing variant ID or stitching set ID');
      return;
    }

    // Disable button while processing
    button.disabled = true;
    const originalText = button.innerHTML;
    button.innerHTML = 'Adding...';

    try {
      // Add damage cover product to cart with stitching set properties
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append('properties[_stitching_set_id]', stitchingSetId);
      formData.append('properties[_is_damage_cover]', 'true');
      formData.append('properties[_stitching_set_type]', 'damage_cover');

      // Add sections to render
      const sectionsToRender = this.getSectionsToRender();
      sectionsToRender.forEach(section => {
        formData.append('sections[]', section.section);
      });
      formData.append('sections_url', window.location.pathname);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add damage cover to cart');
      }

      const responseData = await response.json();
      
      // Update cart sections
      await this.refreshCartSections(responseData);

    } catch (error) {
      console.error('Error adding damage cover:', error);
      this.showNotification('Error adding damage cover', 'error');
      
      // Reset button on error
      button.disabled = false;
      button.innerHTML = originalText;
    }
  }

  async handleStitchingSetRemoval(stitchingSetId) {
    try {
      // Get current cart state
      const currentCart = await this.getCurrentCartState();
      if (!currentCart) return;

      // Find damage cover items for this stitching set
      const damageCoverItems = currentCart.items.filter(item => 
        item.properties &&
        item.properties['_is_damage_cover'] === 'true' &&
        item.properties['_stitching_set_id'] === stitchingSetId
      );

      if (damageCoverItems.length === 0) {
        console.log(`No damage cover found for stitching set: ${stitchingSetId}`);
        return;
      }

      console.log(`Found ${damageCoverItems.length} damage cover item(s) to remove for set: ${stitchingSetId}`);

      // Remove each damage cover item
      for (const damageCoverItem of damageCoverItems) {
        await this.removeLineItem(damageCoverItem.key);
        console.log(`Removed damage cover item: ${damageCoverItem.key}`);
      }

      // Refresh the page to show updated cart
      window.location.reload();

    } catch (error) {
      console.error('Error auto-removing damage cover:', error);
    }
  }

  async removeLineItem(lineItemKey) {
    const formData = new FormData();
    formData.append('id', lineItemKey);
    formData.append('quantity', '0');

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to remove item: ${response.statusText}`);
    }

    return response.json();
  }

  getSectionsToRender() {
    const cartDrawer = document.querySelector('cart-drawer');
    const mainCartId = document.getElementById('main-cart-items')?.dataset.id;
    
    if (cartDrawer) {
      if (mainCartId) {
        return [
          { section: mainCartId, selector: `#shopify-section-${mainCartId} cart-items` },
          { section: "cart-counter", selector: "#shopify-section-cart-counter" },
          { section: "cart-drawer", selector: "#shopify-section-cart-drawer #CartDrawer-Body" }
        ];
      } else {
        return [
          { section: "cart-drawer", selector: "#shopify-section-cart-drawer #CartDrawer-Body" }
        ];
      }
    } else {
      if (mainCartId) {
        return [
          { section: mainCartId, selector: `#shopify-section-${mainCartId} cart-items` },
          { section: 'cart-counter', selector: '#shopify-section-cart-counter' }
        ];
      } else {
        return [];
      }
    }
  }

  async refreshCartSections(cartResponse) {
    // Check if we have sections in the response
    if (cartResponse && cartResponse.sections) {
      // Update each section using the theme's cart system
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && cartDrawer.renderContents && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(cartResponse, true);
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
    } else {
      // Fallback: reload the page
      window.location.reload();
    }

    // Update cart counters
    this.updateCartCounters();
  }

  async updateCartCounters() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const cartCounters = document.querySelectorAll('[data-cart-count]');
      cartCounters.forEach(counter => {
        counter.textContent = cart.item_count;
      });

      const headerCartCounters = document.querySelectorAll('[data-header-cart-count]');
      headerCartCounters.forEach(counter => {
        counter.classList.toggle("hidden", cart.item_count === 0);
      });
    } catch (error) {
      console.error('Error updating cart counters:', error);
    }
  }

  async getCurrentCartState() {
    try {
      const response = await fetch('/cart.js');
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      console.error('Error fetching cart state:', error);
      return null;
    }
  }

  async removeLineItem(lineItemKey) {
    const formData = new FormData();
    formData.append('id', lineItemKey);
    formData.append('quantity', '0');

    const response = await fetch('/cart/change.js', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`Failed to remove item: ${response.statusText}`);
    }

    return response.json();
  }

  showNotification(message, type = 'info') {
    // Try to use existing notification system
    if (window.showNotification && typeof window.showNotification === 'function') {
      window.showNotification(message, type);
      return;
    }

    // Fallback: simple console message
    console.log(`${type.toUpperCase()}: ${message}`);
    
    // Simple visual feedback
    const notification = document.createElement('div');
    notification.className = `cart-notification cart-notification--${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#dc3545' : '#28a745'};
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 10000;
      font-size: 14px;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.damageCoverCart = new DamageCoverCart();
});

// Make DamageCoverCart available globally
window.DamageCoverCart = DamageCoverCart;
